import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * GLOBAL BADGE & BUTTON COLOR SYSTEM
 * 
 * Standardized color styling for all badges, labels, and buttons across the app.
 * This ensures a consistent look and feel throughout the teacher portal.
 * 
 * THEME COLORS:
 * - Light Mode: Black background (#2B2B2B) with white text
 * - Dark Mode: Golden background (hsl(var(--gold-primary))) with white text
 */

/**
 * Get standardized badge/label color classes
 * Use this for all badges, tags, labels, and status indicators
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for the badge
 * 
 * @example
 * <span className={cn("px-2 py-1 rounded", getBadgeColor(isDark))}>
 *   LABEL
 * </span>
 */
export function getBadgeColor(isDark) {
  return isDark 
    ? "bg-[hsl(var(--gold-primary))] text-white" 
    : "bg-[#2B2B2B] text-white";
}

/**
 * Get standardized button color classes for primary actions
 * Use this for all action buttons (Generate, Create, Save, etc.)
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for the button
 * 
 * @example
 * <Button className={cn("h-9 text-sm font-medium rounded-lg", getButtonColor(isDark))}>
 *   Action
 * </Button>
 */
export function getButtonColor(isDark) {
  return isDark
    ? "bg-[hsl(var(--gold-primary))] hover:bg-[hsl(var(--gold-medium))] text-white"
    : "bg-[#2B2B2B] hover:bg-[#1A1A1A] text-white";
}

/**
 * Get standardized toggle/switch color classes (active state)
 * Use this for all toggle switches and switch components
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for the active toggle
 * 
 * @example
 * <button className={cn(
 *   "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
 *   isActive ? getToggleColor(isDark) : "bg-[#D4C4A8]"
 * )}>
 */
export function getToggleColor(isDark) {
  return isDark 
    ? "bg-[hsl(var(--gold-primary))]" 
    : "bg-[#2B2B2B]";
}

/**
 * Get standardized icon background color for card icons/indicators
 * Use this for icon containers in cards and components
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for the icon background
 * 
 * @example
 * <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center border", getIconBgColor(isDark))}>
 */
export function getIconBgColor(isDark) {
  return isDark
    ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))]"
    : "bg-[#D4C4A8] border-[#C9B896]";
}

/**
 * Get standardized icon color
 * Use this for icon colors within containers
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for the icon color
 */
export function getIconColor(isDark) {
  return isDark 
    ? "text-[hsl(var(--foreground))]" 
    : "text-[#2B2B2B]";
}

/**
 * Get standardized inactive/disabled color
 * Use this for inactive toggles, disabled states, etc.
 * 
 * @returns {string} - Tailwind classes for inactive state
 */
export function getInactiveColor() {
  return "bg-[#D4C4A8]";
}

/**
 * Get standardized color for status indicators (dots, circles)
 * Use this for status dots and status indicators
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for the status indicator
 */
export function getStatusIndicatorColor(isDark) {
  return isDark 
    ? "bg-[hsl(var(--gold-primary))]" 
    : "bg-[#2B2B2B]";
}

/**
 * Get standardized color for secondary/descriptive text
 * Use this for "26 students", "45 students", "Grade 7A", etc.
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for secondary text
 * 
 * @example
 * <p className={cn("text-sm font-medium", getSecondaryTextColor(isDark))}>26 students</p>
 */
export function getSecondaryTextColor(isDark) {
  return isDark
    ? "text-[hsl(var(--muted-foreground))]"
    : "text-[#6B6B6B]";
}

/**
 * Get standardized color for descriptive labels
 * Use this for "ENGAGEMENT", "TREND", "Average Score", etc.
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for descriptive label text
 * 
 * @example
 * <span className={cn("text-xs font-medium uppercase", getDescriptiveTextColor(isDark))}>Engagement</span>
 */
export function getDescriptiveTextColor(isDark) {
  return isDark
    ? "text-[hsl(var(--muted-foreground))]"
    : "text-[#8B7B6B]";
}

/**
 * Get standardized color for primary/main text
 * Use this for titles, names, main content
 * 
 * @param {boolean} isDark - Theme mode (true = dark, false = light)
 * @returns {string} - Tailwind classes for primary text
 * 
 * @example
 * <h3 className={cn("text-base font-bold", getPrimaryTextColor(isDark))}>Physics - Grade 7A</h3>
 */
export function getPrimaryTextColor(isDark) {
  return isDark
    ? "text-[hsl(var(--foreground))]"
    : "text-[#1A1A1A]";
}

/**
 * USAGE GUIDE:
 * 
 * 1. FOR BADGES/LABELS:
 *    <span className={cn("px-2 py-1 rounded", getBadgeColor(isDark))}>Label</span>
 * 
 * 2. FOR BUTTONS:
 *    <Button className={cn("h-9 text-sm font-medium rounded-lg", getButtonColor(isDark))}>Action</Button>
 * 
 * 3. FOR TOGGLES (active state):
 *    <button className={cn("relative inline-flex h-6 w-11 rounded-full transition-colors", isActive ? getToggleColor(isDark) : getInactiveColor())}>
 * 
 * 4. FOR ICON CONTAINERS:
 *    <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center border", getIconBgColor(isDark))}>
 *      <Icon className={getIconColor(isDark)} />
 *    </div>
 * 
 * 5. FOR STATUS DOTS:
 *    <div className={cn("w-2 h-2 rounded-full", getStatusIndicatorColor(isDark))}></div>
 * 
 * 6. FOR SECONDARY TEXT (26 students, Grade 7A):
 *    <p className={cn("text-sm font-medium", getSecondaryTextColor(isDark))}>26 students</p>
 * 
 * 7. FOR DESCRIPTIVE LABELS (ENGAGEMENT, TREND):
 *    <span className={cn("text-xs font-medium uppercase", getDescriptiveTextColor(isDark))}>Engagement</span>
 * 
 * 8. FOR PRIMARY TEXT (Titles, names):
 *    <h3 className={cn("text-base font-bold", getPrimaryTextColor(isDark))}>Physics - Grade 7A</h3>
 */
